import { useCallback, useEffect, useMemo, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { ChevronDown, ExternalLink, Search, X } from 'lucide-react';
import { Logo } from './Logo';
import { Tooltip } from '@/components/ui/Tooltip';
import { Skeleton } from '@/components/ui/Skeleton';
import { useCrmInbox } from '@/api/bi/crm';
import { useStoresList } from '@/api/legacy/stores';
import { useAccess } from '@/features/AccessProvider';
import { useVisibleNav, type ResolvedNavGroup, type ResolvedNavItem } from '@/features/nav';
import { useMobileNav } from './MobileNavContext';
import { storage } from '@/lib/storage';
import { cn } from '@/lib/cn';

/**
 * Sidebar.
 *
 * Nav content comes from `features/nav.ts` — this file only renders it. It
 * used to carry its own hardcoded copy of all 49 routes, which drifted from
 * the permission registry that was supposed to describe the same thing.
 */
export function Sidebar() {
  return (
    <aside className="sticky top-0 hidden h-svh max-h-svh min-h-0 w-60 shrink-0 flex-col overflow-hidden border-r border-border bg-surface px-3 py-4 lg:flex">
      <SidebarBody />
    </aside>
  );
}

/**
 * Slide-in drawer used on phones / tablets. Mounted once at the AppShell level
 * and toggled from the Topbar hamburger via the `MobileNavContext`. Reuses the
 * same `SidebarBody` content so the nav is identical to desktop — no second
 * source of truth to drift.
 */
export function MobileSidebarDrawer() {
  const { isOpen, close } = useMobileNav();
  return (
    <DialogPrimitive.Root open={isOpen} onOpenChange={(o) => (o ? null : close())}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className={cn(
            'fixed inset-0 z-[1000] bg-black/50 backdrop-blur-sm lg:hidden',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
            'duration-200 ease-out',
          )}
        />
        <DialogPrimitive.Content
          aria-label="Navigation"
          className={cn(
            'fixed inset-y-0 left-0 z-[1010] flex h-dvh max-h-dvh w-72 max-w-[82vw] flex-col overflow-hidden border-r border-border bg-surface px-3 py-4 shadow-xl lg:hidden',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left',
            'duration-240 ease-out',
          )}
        >
          <DialogPrimitive.Title className="sr-only">Main navigation</DialogPrimitive.Title>
          <DialogPrimitive.Close
            aria-label="Close navigation"
            className="press absolute right-2 top-2 rounded-md p-1.5 text-muted-foreground hover:text-foreground focus-ring"
          >
            <X className="h-4 w-4" aria-hidden />
          </DialogPrimitive.Close>
          <SidebarBody onNavigate={close} />
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

/**
 * Shared sidebar contents — Logo + filter + grouped nav + footer. Pulled out
 * of the `<aside>` so the same body powers the desktop rail and the mobile
 * drawer.
 */
function SidebarBody({ onNavigate }: { onNavigate?: () => void }) {
  const { groups, isReady } = useNavWithCounts();
  const [query, setQuery] = useState('');
  const { expanded, toggle } = useSectionState(groups);

  const filtered = useMemo(() => filterGroups(groups, query), [groups, query]);
  const searching = query.trim().length > 0;

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
      <div className="shrink-0 px-2 pb-3">
        <Logo />
      </div>

      <div className="shrink-0 px-1 pb-3">
        <NavFilter value={query} onChange={setQuery} />
      </div>

      <nav
        aria-label="Main navigation"
        className="min-h-0 flex-1 space-y-4 overflow-y-auto overflow-x-hidden overscroll-contain py-0.5"
      >
        {!isReady ? (
          <NavSkeleton />
        ) : filtered.length === 0 ? (
          <p className="px-3 py-6 text-center text-xs text-muted-foreground">
            Nothing matches <span className="font-medium text-foreground">&ldquo;{query.trim()}&rdquo;</span>.
          </p>
        ) : (
          filtered.map((group) => (
            <SidebarGroup
              key={group.id}
              group={group}
              // While searching, open everything so matches buried in
              // collapsed sections are actually reachable.
              collapsed={searching ? false : !expanded.has(group.id)}
              onToggle={() => toggle(group.id)}
              onNavigate={onNavigate}
            />
          ))
        )}
      </nav>

      <SidebarFooter />
    </div>
  );
}

function NavFilter({ value, onChange }: { value: string; onChange: (next: string) => void }) {
  return (
    <div className="relative">
      <Search
        className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground"
        aria-hidden
      />
      <input
        type="search"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Filter navigation…"
        aria-label="Filter navigation"
        className="h-8 w-full rounded-md border border-border bg-surface-elevated pl-8 pr-7 text-xs text-foreground outline-none placeholder:text-muted-foreground focus-ring"
      />
      {value ? (
        <button
          type="button"
          onClick={() => onChange('')}
          aria-label="Clear navigation filter"
          className="absolute right-2 top-1/2 -translate-y-1/2 rounded text-muted-foreground hover:text-foreground focus-ring"
        >
          <X className="h-3 w-3" aria-hidden />
        </button>
      ) : null}
    </div>
  );
}

function NavSkeleton() {
  return (
    <div className="space-y-4" aria-hidden>
      {[5, 4, 3].map((rows, i) => (
        <div key={i} className="space-y-1.5">
          <Skeleton className="mx-2 h-3 w-24" />
          {Array.from({ length: rows }).map((__, r) => (
            <Skeleton key={r} className="h-8 w-full" />
          ))}
        </div>
      ))}
    </div>
  );
}

function filterGroups(groups: ResolvedNavGroup[], query: string): ResolvedNavGroup[] {
  const needle = query.trim().toLowerCase();
  if (!needle) return groups;
  const out: ResolvedNavGroup[] = [];
  for (const group of groups) {
    const groupMatches = group.label.toLowerCase().includes(needle);
    const items = group.items.filter(
      (item) => groupMatches || item.label.toLowerCase().includes(needle) || item.path.toLowerCase().includes(needle),
    );
    if (items.length) out.push({ ...group, items });
  }
  return out;
}

/**
 * Which sections are open.
 *
 * Two behaviours the old implementation lacked: the choice survives a reload,
 * and the section owning the current route opens itself so you can always see
 * where you are.
 */
function useSectionState(groups: ResolvedNavGroup[]) {
  const { pathname } = useLocation();

  const activeGroupId = useMemo(() => {
    let best: { id: string; length: number } | null = null;
    for (const group of groups) {
      for (const item of group.items) {
        if (item.external) continue;
        const matches = item.exact ? pathname === item.path : pathname === item.path || pathname.startsWith(`${item.path}/`);
        if (matches && (!best || item.path.length > best.length)) {
          best = { id: group.id, length: item.path.length };
        }
      }
    }
    return best?.id ?? null;
  }, [groups, pathname]);

  const [expanded, setExpanded] = useState<Set<string>>(() => {
    const stored = storage.getNavSections();
    if (storage.hasNavSections()) return new Set(stored);
    // First visit: open the dashboards rail rather than presenting a wall of
    // closed accordions with no indication anything is inside them.
    return new Set(['dashboards']);
  });

  useEffect(() => {
    if (!activeGroupId) return;
    setExpanded((prev) => {
      if (prev.has(activeGroupId)) return prev;
      const next = new Set(prev);
      next.add(activeGroupId);
      storage.setNavSections(Array.from(next));
      return next;
    });
  }, [activeGroupId]);

  const toggle = useCallback((id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      storage.setNavSections(Array.from(next));
      return next;
    });
  }, []);

  return { expanded, toggle };
}

/**
 * Nav entries plus live counters. Today that is the CRM inbox: assignment
 * notifications used to arrive only as a websocket toast, so anyone who was
 * not looking at the tab when a ticket landed had no way to know — they had
 * to go find it. The pill is the durable version of that signal.
 */
function useNavWithCounts(): { groups: ResolvedNavGroup[]; isReady: boolean } {
  const { groups, isReady } = useVisibleNav();
  const { can, isSuperuser } = useAccess();
  const showsInbox = groups.some((g) => g.items.some((i) => i.slug === 'crm.inbox'));
  const mayReadInbox = showsInbox && (isSuperuser || can('crm.inbox'));
  const inbox = useCrmInbox(true, mayReadInbox);
  const unread = inbox.data?.results.length ?? 0;

  return useMemo(() => {
    if (!unread) return { groups, isReady };
    return {
      isReady,
      groups: groups.map((group) => ({
        ...group,
        items: group.items.map((item) => (item.slug === 'crm.inbox' ? { ...item, count: unread } : item)),
      })),
    };
  }, [groups, isReady, unread]);
}

function SidebarGroup({
  group,
  collapsed,
  onToggle,
  onNavigate,
}: {
  group: ResolvedNavGroup;
  collapsed: boolean;
  onToggle: () => void;
  onNavigate?: () => void;
}) {
  if (group.items.length === 0) return null;
  const panelId = `nav-section-${group.id}`;
  return (
    <div className="space-y-1">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={!collapsed}
        aria-controls={panelId}
        className="press flex w-full items-center gap-1.5 rounded-md px-2 py-1 text-left text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground hover:bg-muted/80 hover:text-foreground focus-ring"
      >
        <ChevronDown
          className={cn(
            'h-3.5 w-3.5 shrink-0 text-muted-foreground/80 transition-transform duration-200 ease-out',
            collapsed && '-rotate-90',
          )}
          aria-hidden
        />
        <span className="min-w-0 flex-1 truncate px-0.5">{group.label}</span>
        {collapsed ? <GroupBadge items={group.items} /> : null}
      </button>
      {!collapsed ? (
        <ul id={panelId} className="space-y-0.5">
          {group.items.map((item) => (
            <li key={item.path}>
              <SidebarLink item={item} onNavigate={onNavigate} />
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

/** Surfaces unread counts hidden inside a collapsed section. */
function GroupBadge({ items }: { items: ResolvedNavItem[] }) {
  const total = items.reduce((sum, item) => sum + (item.count ?? 0), 0);
  if (!total) return null;
  return <CountPill count={total} />;
}

const LINK_CLASS =
  'press group relative flex items-center gap-2.5 rounded-md px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-ring';

function SidebarLink({ item, onNavigate }: { item: ResolvedNavItem; onNavigate?: () => void }) {
  const Icon = item.icon;
  const body = (
    <>
      <Icon className="h-4 w-4 shrink-0 transition-transform duration-150 ease-out group-hover:scale-105" />
      <span className="flex-1">{item.label}</span>
      {item.count ? <CountPill count={item.count} /> : null}
      {item.fresh ? <FreshSticker /> : null}
      {item.external ? <ExternalLink className="h-3 w-3 shrink-0 opacity-60" aria-hidden /> : null}
    </>
  );

  // `/wall` renders outside the app shell, so an in-app navigation would drop
  // the user on a chrome-less page with no way back. Open it in a new tab.
  if (item.external) {
    return (
      <Tooltip content="Opens in a new tab">
        <a href={item.path} target="_blank" rel="noreferrer" className={LINK_CLASS}>
          {body}
        </a>
      </Tooltip>
    );
  }

  return (
    <NavLink
      to={item.path}
      end={item.exact}
      onClick={onNavigate}
      className={({ isActive }) =>
        cn(LINK_CLASS, isActive && 'bg-primary/10 text-primary hover:bg-primary/15 hover:text-primary')
      }
    >
      {body}
    </NavLink>
  );
}

function CountPill({ count }: { count: number }) {
  return (
    <span
      className="inline-flex min-w-4 items-center justify-center rounded-full bg-primary px-1.5 py-0.5 text-[10px] font-semibold leading-none text-primary-foreground"
      aria-label={`${count} unacknowledged`}
    >
      {count > 99 ? '99+' : count}
    </span>
  );
}

/**
 * A pinch of personality: tiny rotated "NEW" sticker. Driven by a ship date
 * rather than a boolean, so it actually expires — the flag version left 27 of
 * 49 entries permanently badged, which taught everyone to ignore it.
 */
function FreshSticker() {
  return (
    <span
      className="sticker-rotate inline-flex items-center rounded-sm bg-primary px-1 text-[8px] font-bold uppercase tracking-[0.12em] text-primary-foreground shadow-[0_1px_0_rgba(0,0,0,0.15)]"
      aria-label="New"
    >
      new
    </span>
  );
}

/**
 * Sidebar footer with a quiet "live system" pulse — counts the stores
 * currently online so on-call staff have an at-a-glance signal without
 * leaving the current page. Falls back to a faint placeholder during boot.
 */
function SidebarFooter() {
  const { data: stores = [], isLoading } = useStoresList();

  const { online, total } = useMemo(() => {
    const t = stores.length;
    const o = stores.filter((s) => s.is_online).length;
    return { online: o, total: t };
  }, [stores]);

  const tone = !total
    ? 'live-dot'
    : online === 0
      ? 'live-dot live-dot-danger'
      : online < total
        ? 'live-dot live-dot-warning'
        : 'live-dot';

  const label = isLoading
    ? 'Booting…'
    : total === 0
      ? 'No stores yet'
      : `${online} of ${total} stores live`;

  return (
    <div className="shrink-0 space-y-2 px-2 pt-4">
      <Tooltip content="Updates every minute via /api/stores/online/">
        <div className="flex items-center gap-2 rounded-md border border-border bg-surface-elevated px-2.5 py-1.5 text-[11px] text-foreground">
          <span className={cn(tone)} aria-hidden />
          <span className="truncate">{label}</span>
        </div>
      </Tooltip>
      <p className="px-1 text-[10px] text-muted-foreground">
        Fasto BI · v0.1
        <span className="ml-1 opacity-60">· Asia/Kathmandu</span>
      </p>
    </div>
  );
}
